/**
 * The Tools panel's Team section (web): bring a second interpreter into this
 * call as its team interpreter (docs/interpreter-teaming.md). The panel owns
 * the call's team (useTeamTool, team.ts) and passes it here, so the Tools key
 * and this section read the same state.
 *
 * The call's interpreter asks anyone available, or one colleague from the
 * transfer picker's list for this call (GET /api/interpreter/transfer-candidates
 * ?callId=, the same server rule), waits, and can end the team. The teammate
 * sees only Leave team.
 */
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { apiClient } from '../../../shared/api-client';
import { shiftAccountKey, shiftRequest } from '../../../vrs-layout/components/useShiftBreaks';
const candidatesSchema = z.object({ candidates: z.array(z.object({
        id: z.string().min(1).max(120), languages: z.array(z.string()).optional()
    })) });
const useStyles = makeStyles()(theme => ({
    controls: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
    button: { transition: 'none', border: `1px solid ${theme.palette.ui04}`, minHeight: 48, width: '100%', whiteSpace: 'normal', textAlign: 'start',
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: 2 } },
    line: { ...withPixelLineHeight(theme.typography.bodyShortRegular), margin: `0 0 ${theme.spacing(2)}`, overflowWrap: 'anywhere' }
}));
/**
 * @param {Object} props - The props.
 * @param {string} props.callId - This room's queue call.
 * @param {boolean} props.open - Whether the Tools panel is open.
 * @param {ITeamTool} props.team - The call's team (the panel's useTeamTool).
 * @returns {ReactElement}
 */
export default function TeamSection({ callId, open, team }) {
    const { classes } = useStyles();
    const asking = team.state.phase === 'idle' || team.state.phase === 'left';
    const [colleagues, setColleagues] = useState(null);
    const load = useRef(null);
    useEffect(() => {
        if (!open || !asking || team.teammate) {
            return undefined;
        }
        const controller = new AbortController();
        const account = shiftAccountKey();
        load.current?.abort();
        load.current = controller;
        (async () => {
            try {
                if (!account)
                    throw new Error('Interpreter profile unavailable');
                const response = await shiftRequest(apiClient.get(`/api/interpreter/transfer-candidates?callId=${encodeURIComponent(callId)}`, { signal: controller.signal, retryAuth: false }), controller.signal);
                const parsed = candidatesSchema.safeParse(response.data);
                if (!response.error && response.status >= 200 && response.status < 300 && parsed.success
                    && !controller.signal.aborted && shiftAccountKey() === account) {
                    setColleagues({ callId, rows: parsed.data.candidates });
                }
            }
            catch {
                // Request team (anyone available) still works without the list.
            }
        })();
        return () => controller.abort();
    }, [asking, callId, open, team.teammate]);
    const rows = colleagues?.callId === callId ? colleagues.rows : [];
    return (React.createElement("div", { className: classes.controls },
        team.line && React.createElement("div", { "aria-live": 'polite', className: classes.line }, team.line),
        team.state.phase === 'teammate' || team.state.phase === 'teamed'
            ? React.createElement(Button, { className: classes.button, label: team.teammate ? 'Leave team' : 'End team', onClick: team.end, type: BUTTON_TYPES.TERTIARY })
            : team.state.phase === 'pending'
                ? React.createElement(Button, { accessibilityLabel: 'Cancel team request', className: classes.button, label: 'Cancel', onClick: team.cancel, type: BUTTON_TYPES.TERTIARY })
                : asking && React.createElement(React.Fragment, null,
                    React.createElement(Button, { className: classes.button, label: 'Request team', onClick: () => team.request(), type: BUTTON_TYPES.PRIMARY }),
                    rows.map((candidate, index) => (React.createElement(Button, { accessibilityLabel: `Request team with available interpreter ${index + 1}`, className: classes.button, key: candidate.id, label: `Available interpreter ${index + 1}${candidate.languages?.length ? ` · ${candidate.languages.join(', ')}` : ''}`, onClick: () => team.request(candidate.id), type: BUTTON_TYPES.TERTIARY }))))));
}
